// The reaction bar, shared by the group feed and the event chat thread.
//
// Extracted from feed.ts on 2026-09-09 when chat gained reactions. It was moved
// verbatim rather than rewritten: every comment below records a real bug the
// feed version already paid for, and a "tidy" reimplementation would have
// reintroduced them on the chat surface one at a time.
//
// The two callers differ ONLY in which endpoints they hit and which id they
// carry, so that is the whole of the injected surface — `put` and `remove`. The
// bar never learns whether it is sitting on a post or a message.

import { el } from '../dom.js';
import {
  REACTION_KINDS,
  REACTION_LABELS,
  type ReactionKind,
  type ReactionRoster,
} from '@msc/shared';
import { ApiClientError } from '../api/client.js';

/**
 * Icon set shipped 2026-08-30 (see the ChatGPT-generation prompt in that
 * session log). Served straight off the SPA's own origin from
 * web/public/reactions/, matching how logo.png already works.
 */
const REACTION_ICON: Record<ReactionKind, string> = {
  eye: '/reactions/eye.png',
  thumbs_up: '/reactions/thumbs_up.png',
  thumbs_down: '/reactions/thumbs_down.png',
  laugh: '/reactions/laugh.png',
  confetti: '/reactions/confetti.png',
  excited: '/reactions/excited.png',
  caring: '/reactions/caring.png',
  prayer: '/reactions/prayer.png',
};

export function reactionImg(kind: ReactionKind, cls = 'reaction-icon-img'): HTMLElement {
  return el('img', { src: REACTION_ICON[kind], alt: REACTION_LABELS[kind], class: cls });
}

/**
 * Anything that carries a roster: a `FeedPost` or a `ChatMessage`, structurally.
 *
 * The bar WRITES `reactions` back on this object after a successful call, which
 * is what lets it rebuild itself from the same model the caller holds — and what
 * makes the caller's own live-update path (which also assigns to `reactions`)
 * agree with it rather than fight it.
 */
export interface ReactionTarget {
  reactions: ReactionRoster;
}

export interface ReactionApi {
  put(kind: ReactionKind): Promise<ReactionRoster>;
  remove(): Promise<ReactionRoster>;
}

/** Which reaction the viewer picked on this target, if any. */
export function myReaction(roster: ReactionRoster, viewerId: number | null): ReactionKind | null {
  if (viewerId === null) return null;
  for (const kind of REACTION_KINDS) {
    if (roster[kind]?.some((u) => u.userId === viewerId)) return kind;
  }
  return null;
}

/**
 * Tap = eye, or removes your current reaction if you already have one.
 * The full picker opens on press-and-hold, and additionally on hover where a
 * real pointer exists. See docs/domain.md, "Feed and posts", for the design
 * decision this implements.
 */
export function reactionBar(
  target: ReactionTarget,
  viewerId: number | null,
  reactionApi: ReactionApi,
): HTMLElement {
  const mine = myReaction(target.reactions, viewerId);

  const tap = el(
    'button',
    { type: 'button', class: mine ? 'reaction-tap reaction-mine' : 'reaction-tap' },
    reactionImg(mine ?? 'eye'),
  );

  const counts = el(
    'div',
    { class: 'reaction-counts' },
    REACTION_KINDS.filter((k) => (target.reactions[k]?.length ?? 0) > 0).map((k) => {
      const users = target.reactions[k]!;
      const chip = el('span', { class: 'reaction-count' }, [
        reactionImg(k, 'reaction-icon-img reaction-icon-img-sm'),
        ` ${users.length}`,
      ]);
      chip.setAttribute('title', users.map((u) => u.displayName).join(', '));
      return chip;
    }),
  );

  const picker = el('div', { class: 'reaction-picker', hidden: true }, []);
  const bar = el('div', { class: 'reaction-bar' }, [tap, picker, counts]);

  async function apply(kind: ReactionKind): Promise<void> {
    closePicker();
    const removing = kind === mine;
    try {
      target.reactions = removing ? await reactionApi.remove() : await reactionApi.put(kind);
      bar.replaceWith(reactionBar(target, viewerId, reactionApi));
    } catch (err) {
      window.alert(err instanceof ApiClientError ? err.message : 'Could not save that reaction.');
    }
  }

  // How the picker got opened decides whether a click on the tap button still
  // counts. A long-press opens it mid-click, so honouring that click would both
  // open the picker AND apply `eye`. Hover is the opposite: on a mouse the
  // picker is open the whole time the pointer rests on the button, so treating
  // that the same way would swallow every click and make `eye` unreachable.
  let openedByHold = false;

  function onDocumentClick(ev: MouseEvent): void {
    if (!bar.contains(ev.target as Node)) closePicker();
  }

  /**
   * Keep the picker inside whatever clips it.
   *
   * On the feed this never does anything: a post is a full-width card, the bar
   * sits at its left, and the only clipper is the viewport. The chat thread
   * breaks BOTH assumptions, and each break hides the picker completely with no
   * error and no visual hint that anything is wrong:
   *
   *   • `.chat-list` is a `max-height: 60vh` overflow scroller, so the default
   *     `bottom: 100%` placement puts the picker above the scroller's top edge
   *     on every message near the top of the view. Flipped below when the space
   *     above is too small.
   *   • A `.chat-mine` bubble is right-aligned and only 85% wide, so a picker
   *     anchored to its left edge runs past the scroller's RIGHT edge — 15px on
   *     a 1280px window, more as the bubble narrows. Shifted back by exactly the
   *     overflow.
   *
   * Both are measured on the laid-out picker rather than assumed: the icon row's
   * size is CSS-driven and any hardcoded guess drifts the next time it changes.
   */
  function placePicker(): void {
    // The bar is rebuilt on every apply, but the picker is re-opened in place by
    // hover — so last open's shift has to be cleared before measuring this one.
    picker.style.left = '';
    picker.classList.remove('reaction-picker-below');

    const clipper = bar.closest('.chat-list') ?? document.documentElement;
    const clip = clipper.getBoundingClientRect();
    const box = picker.getBoundingClientRect();

    if (bar.getBoundingClientRect().top - clip.top < box.height + 8) {
      picker.classList.add('reaction-picker-below');
    }

    const overflow = box.right - clip.right;
    if (overflow > 0) picker.style.left = `${-Math.ceil(overflow) - 4}px`;
  }

  function openPicker(viaHold: boolean): void {
    openedByHold = viaHold;
    picker.replaceChildren(
      ...REACTION_KINDS.map((kind) => {
        const btn = el(
          'button',
          { type: 'button', class: kind === mine ? 'reaction-mine' : '' },
          reactionImg(kind),
        );
        btn.setAttribute('title', REACTION_LABELS[kind]);
        btn.addEventListener('click', (ev) => {
          ev.stopPropagation();
          void apply(kind);
        });
        return btn;
      }),
    );
    picker.hidden = false;
    placePicker();
    // Paired with the removal in `closePicker`. It cannot be a `{ once: true }`
    // listener: hover opens and closes this repeatedly, so each open would leave
    // another one-shot listener behind to fire on some unrelated later click.
    document.addEventListener('click', onDocumentClick);
  }

  function closePicker(): void {
    picker.hidden = true;
    openedByHold = false;
    document.removeEventListener('click', onDocumentClick);
  }

  // Plain tap: eye by default, or removes whatever the viewer currently has.
  tap.addEventListener('click', () => {
    if (!picker.hidden && openedByHold) return; // A long-press just opened it.
    void apply(mine ?? 'eye');
  });

  // Long-press opens the full picker. Timer-based rather than a native
  // `contextmenu`/`touchstart` combo, so mouse and touch share one path.
  let pressTimer: ReturnType<typeof setTimeout> | null = null;
  function startPress(): void {
    pressTimer = setTimeout(() => openPicker(true), 500);
  }
  function cancelPress(): void {
    if (pressTimer) clearTimeout(pressTimer);
    pressTimer = null;
  }
  tap.addEventListener('pointerdown', startPress);
  tap.addEventListener('pointerup', cancelPress);
  tap.addEventListener('pointerleave', cancelPress);
  tap.addEventListener('contextmenu', (ev) => ev.preventDefault()); // long-press on mobile

  // Hover also opens it, but only where a pointer can actually hover. Touch
  // keeps the long-press as its only path, so this is an addition, not a
  // replacement — `(hover: hover)` alone would still match some stylus and
  // hybrid devices, hence the `(pointer: fine)` half.
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    let openTimer: ReturnType<typeof setTimeout> | null = null;
    let closeTimer: ReturnType<typeof setTimeout> | null = null;
    const clearTimers = (): void => {
      if (openTimer) clearTimeout(openTimer);
      if (closeTimer) clearTimeout(closeTimer);
      openTimer = null;
      closeTimer = null;
    };

    // Opening on a short delay rather than instantly, so scrolling the feed with
    // the cursor resting over it does not pop a picker on every post it crosses.
    tap.addEventListener('mouseenter', () => {
      clearTimers();
      if (picker.hidden) openTimer = setTimeout(() => openPicker(false), 200);
    });
    tap.addEventListener('mouseleave', () => {
      if (openTimer) clearTimeout(openTimer);
      openTimer = null;
    });

    // The picker floats ABOVE the button with a gap between the two, so a pointer
    // travelling from one to the other is briefly over neither and fires
    // `mouseleave` on the bar. Closing on a delay bridges that gap; without it
    // the picker is unreachable with a mouse.
    bar.addEventListener('mouseenter', clearTimers);
    bar.addEventListener('mouseleave', () => {
      clearTimers();
      closeTimer = setTimeout(closePicker, 250);
    });
  }

  return bar;
}
